import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getApiUrl, addUserParam } from '../config/api.config';

// NOTE: Do NOT call getApiUrl() at module load time!
// It must be called at request time when window.location is available.

// Fetch yield data for a ticker
export const fetchYields = createAsyncThunk(
  'yields/fetchYields',
  async ({ ticker, granularity }, { rejectWithValue }) => {
    try {
      const response = await fetch(
        addUserParam(`${getApiUrl()}/yields/${ticker}?granularity=${granularity}`),
        {
          credentials: 'include'
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch yield data');
      }

      const data = await response.json();
      return {
        ticker,
        ...data
      };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Legacy async thunk for backward compatibility
export const fetchYieldData = createAsyncThunk(
  'yields/fetchData',
  async ({ ticker, timeRange }) => {
    const response = await fetch(
      addUserParam(`${getApiUrl()}/yields/${ticker}?timeRange=${timeRange}`),
      {
        credentials: 'include'
      }
    );

    if (!response.ok) {
      throw new Error('Failed to fetch yield data');
    }

    return response.json();
  }
);

export const fetchBatchYields = createAsyncThunk(
  'yields/fetchBatch',
  async ({ tickers }) => {
    const response = await fetch(
      addUserParam(`${getApiUrl()}/yields/batch`),
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ tickers })
      }
    );

    if (!response.ok) {
      throw new Error('Failed to fetch batch yields');
    }

    return response.json();
  }
);

// Yields slice
const yieldsSlice = createSlice({
  name: 'yields',
  initialState: {
    yields: {}, // Store yields by ticker
    yieldData: {},
    batchYields: {},
    loading: false,
    error: null,
  },
  reducers: {
    clearYieldData: (state) => {
      state.yieldData = {};
      state.error = null;
    },
    updateYieldData: (state, action) => {
      // Update yields with real-time WebSocket data
      // Using Object.assign for better performance (Immer handles immutability)
      Object.assign(state.yields, action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch yields
      .addCase(fetchYields.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchYields.fulfilled, (state, action) => {
        state.loading = false;
        const { ticker, ...yieldData } = action.payload;
        state.yields[ticker] = yieldData;
      })
      .addCase(fetchYields.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch yield data (legacy)
      .addCase(fetchYieldData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchYieldData.fulfilled, (state, action) => {
        state.loading = false;
        state.yieldData[action.payload.ticker] = action.payload;
      })
      .addCase(fetchYieldData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Fetch batch yields
      .addCase(fetchBatchYields.fulfilled, (state, action) => {
        state.batchYields = action.payload.yields;
      });
  },
});

export const { clearYieldData, updateYieldData } = yieldsSlice.actions;
export default yieldsSlice.reducer;